import React from 'react';
import { Calendar, DollarSign, FileText, Lock, Unlock } from 'lucide-react';
import { ProjectUnlockData } from '@/types/project';
import { formatCompactCurrency, formatCompactNumber } from '@/utils';
import UnlockPieChart from './UnlockPieChart';
import UnlockTimelineChart from './UnlockTimelineChart';

interface ProjectUnlocksMobileProps {
  unlockData: ProjectUnlockData | null;
}

const ProjectUnlocksMobile: React.FC<ProjectUnlocksMobileProps> = ({ unlockData }) => {
  if (!unlockData) {
    return (
      <div className="py-12 text-center">
        <p className="font-['Plus_Jakarta_Sans'] text-[rgba(45,41,35,0.64)] dark:text-[rgba(253,253,253,0.64)]">
          No unlock data available
        </p>
      </div>
    );
  }

  const formatDate = (dateStr: string) => new Date(dateStr).toLocaleDateString('en-GB');

  const calculateTimelineProgress = () => {
    const now = Date.now();
    const vestingStart = new Date(unlockData.vesting_start_date).getTime();
    const vestingEnd = new Date(unlockData.vesting_end_date).getTime();

    if (now < vestingStart) return 0;
    if (now >= vestingEnd) return 100;

    return Math.min(Math.max(((now - vestingStart) / (vestingEnd - vestingStart)) * 100, 0), 100);
  };

  const calculateUnlockProgress = (tgeUnlockPercent?: number) => {
    const now = Date.now();
    const vestingStart = new Date(unlockData.vesting_start_date).getTime();
    const vestingEnd = new Date(unlockData.vesting_end_date).getTime();
    const tgePercent = tgeUnlockPercent || 0;

    if (now < vestingStart) return Math.min(tgePercent, 100);
    if (now >= vestingEnd) return 100;

    const totalVestingTime = vestingEnd - vestingStart;
    const elapsedTime = now - vestingStart;
    const timeBasedProgress = (elapsedTime / totalVestingTime) * (100 - tgePercent);

    return Math.min(Math.max(tgePercent + timeBasedProgress, 0), 100);
  };

  const renderCurrencyWithMutedDollar = (value: string) => {
    const trimmedValue = value.trim();
    if (!trimmedValue.startsWith('$')) return trimmedValue;

    return (
      <>
        <span className="text-[rgba(45,41,35,0.48)] dark:text-[rgba(253,253,253,0.48)]">$</span>{' '}
        {trimmedValue.slice(1)}
      </>
    );
  };

  const renderMetricValue = (amount: number | null | undefined, allowDash = false) => {
    if (allowDash && (amount == null || amount <= 0)) {
      return '—';
    }

    return renderCurrencyWithMutedDollar(formatCompactCurrency(amount || 0));
  };

  const timelineProgress = calculateTimelineProgress();
  const totalSupply = unlockData.total_supply || 1;
  const unlockedPercent = ((unlockData.total_unlocked || 0) / totalSupply * 100).toFixed(1);
  const lockedPercent = ((unlockData.total_locked || 0) / totalSupply * 100).toFixed(1);

  return (
    <div className="space-y-6">
      <div className="h-[117px] bg-white px-5 pt-5 pb-[11px] dark:bg-[#1c1c1c]">
        <div className="flex items-center justify-between">
          <span className="font-['Plus_Jakarta_Sans'] text-[14px] font-medium leading-[20px] text-[#2d2923] dark:text-[#fdfdfd]">
            {formatDate(unlockData.vesting_start_date)}
          </span>
          <span className="font-['Plus_Jakarta_Sans'] text-[14px] font-medium leading-[20px] text-[#2d2923] dark:text-[#fdfdfd]">
            {formatDate(unlockData.vesting_end_date)}
          </span>
        </div>
        <div className="relative mt-5 h-[11px] rounded-[8px] bg-[rgba(45,41,35,0.12)] dark:bg-[rgba(253,253,253,0.12)]">
          <div
            className="absolute left-0 top-0 h-[11px] rounded-[8px] bg-[#2d2923] dark:bg-[#fdfdfd]"
            style={{ width: `${timelineProgress}%` }}
          />
          <div
            className="absolute top-0 h-[11px] w-[6px] -translate-x-1/2 rounded-[8px] bg-[#2d2923] dark:bg-[#fdfdfd]"
            style={{ left: `${timelineProgress}%` }}
          />
        </div>
        <div className="relative mt-[11px] h-[18px]">
          <span className="absolute left-0 font-['Plus_Jakarta_Sans'] text-[13px] font-medium leading-[18px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]">
            Vesting Start
          </span>
          <span className="absolute left-1/2 -translate-x-1/2 font-['Plus_Jakarta_Sans'] text-[13px] font-medium leading-[18px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]">
            Current
          </span>
          <span className="absolute right-0 font-['Plus_Jakarta_Sans'] text-[13px] font-medium leading-[18px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]">
            Vesting End
          </span>
        </div>
      </div>

      <div className="h-[411px] border border-white bg-[rgba(255,255,255,0.72)] p-5 dark:border-[#171717] dark:bg-[rgba(23,23,23,0.72)]">
        <h2 className="font-['Plus_Jakarta_Sans'] text-[20px] font-semibold leading-[24px] tracking-[-0.2px] text-[#2d2923] dark:text-[#fdfdfd]">
          Token Distribution
        </h2>
        <div className="mx-auto mt-4 h-[171px] w-[171px]">
          <UnlockPieChart unlockData={unlockData} variant="mobile" />
        </div>
        <div className="mt-5 grid grid-cols-2 gap-2">
          <div className="border border-[rgba(45,41,35,0.12)] bg-white p-5 dark:border-[rgba(253,253,253,0.12)] dark:bg-[#1c1c1c]">
            <div className="mb-[18px] flex items-center gap-3">
              <div className="h-[8px] w-[19px] bg-[#c4e804]" />
              <span className="font-['Plus_Jakarta_Sans'] text-[12px] font-medium leading-[16px] text-[#2d2923] dark:text-[#fdfdfd]">
                Unlocked
              </span>
            </div>
            <p className="font-['Plus_Jakarta_Sans'] text-[18px] font-medium leading-[24px] tracking-[-0.2px] text-[#2d2923] dark:text-[#fdfdfd]">
              {formatCompactNumber(unlockData.total_unlocked)}
            </p>
            <p className="font-['Plus_Jakarta_Sans'] text-[12px] font-normal leading-[16px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]">
              {unlockedPercent}% of supply
            </p>
          </div>
          <div className="border border-[rgba(45,41,35,0.12)] bg-white p-5 dark:border-[rgba(253,253,253,0.12)] dark:bg-[#1c1c1c]">
            <div className="mb-[18px] flex items-center gap-3">
              <div className="h-[8px] w-[19px] bg-[#6b6969]" />
              <span className="font-['Plus_Jakarta_Sans'] text-[12px] font-medium leading-[16px] text-[#2d2923] dark:text-[#fdfdfd]">
                Locked
              </span>
            </div>
            <p className="font-['Plus_Jakarta_Sans'] text-[18px] font-medium leading-[24px] tracking-[-0.2px] text-[#2d2923] dark:text-[#fdfdfd]">
              {formatCompactNumber(unlockData.total_locked)}
            </p>
            <p className="font-['Plus_Jakarta_Sans'] text-[12px] font-normal leading-[16px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]">
              {lockedPercent}% of supply
            </p>
          </div>
        </div>
      </div>

      <div className="h-[494px] border border-white bg-[rgba(255,255,255,0.72)] p-4 dark:border-[#171717] dark:bg-[rgba(23,23,23,0.72)]">
        <h2 className="font-['Plus_Jakarta_Sans'] text-[20px] font-semibold leading-[24px] tracking-[-0.2px] text-[#2d2923] dark:text-[#fdfdfd]">
          Unlock Timeline
        </h2>
        <div className="mt-4">
          <UnlockTimelineChart unlockData={unlockData} variant="mobile" />
        </div>
        <div className="mt-4 grid grid-cols-2 gap-3">
          <div className="border border-[rgba(45,41,35,0.12)] bg-white px-5 py-4 dark:border-[rgba(253,253,253,0.12)] dark:bg-[#1c1c1c]">
            <span className="font-['Plus_Jakarta_Sans'] text-[13px] font-medium leading-[18px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]">
              Vesting Start
            </span>
            <p className="mt-3 font-['Plus_Jakarta_Sans'] text-[16px] font-semibold leading-[24px] text-[#2d2923] dark:text-[#fdfdfd]">
              {formatDate(unlockData.vesting_start_date)}
            </p>
          </div>
          <div className="border border-[rgba(45,41,35,0.12)] bg-white px-5 py-4 dark:border-[rgba(253,253,253,0.12)] dark:bg-[#1c1c1c]">
            <span className="font-['Plus_Jakarta_Sans'] text-[13px] font-medium leading-[18px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]">
              Vesting End
            </span>
            <p className="mt-3 font-['Plus_Jakarta_Sans'] text-[16px] font-semibold leading-[24px] text-[#2d2923] dark:text-[#fdfdfd]">
              {formatDate(unlockData.vesting_end_date)}
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-3">
        <div className="flex h-[56px] items-center gap-3 overflow-hidden bg-white p-4 dark:bg-[#1c1c1c]">
          <div className="flex min-w-0 flex-1 items-center gap-[6px]">
            <DollarSign size={16} className="text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]" />
            <span className="whitespace-nowrap font-['Plus_Jakarta_Sans'] text-[13px] font-semibold leading-[18px] text-[#2d2923] dark:text-[#fdfdfd]">
              Total Supply
            </span>
          </div>
          <span className="font-['Plus_Jakarta_Sans'] text-[18px] font-medium leading-[24px] tracking-[-0.2px] text-[#2d2923] dark:text-[#fdfdfd]">
            {renderMetricValue(unlockData.total_supply)}
          </span>
        </div>

        <div className="flex h-[56px] items-center gap-3 overflow-hidden bg-white p-4 dark:bg-[#1c1c1c]">
          <div className="flex min-w-0 flex-1 items-center gap-[6px]">
            <FileText size={16} className="text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]" />
            <span className="whitespace-nowrap font-['Plus_Jakarta_Sans'] text-[13px] font-semibold leading-[18px] text-[#2d2923] dark:text-[#fdfdfd]">
              Circulating Supply
            </span>
          </div>
          <span className="font-['Plus_Jakarta_Sans'] text-[18px] font-medium leading-[24px] tracking-[-0.2px] text-[#2d2923] dark:text-[#fdfdfd]">
            {renderMetricValue(unlockData.circulating_supply, true)}
          </span>
        </div>

        <div className="flex h-[56px] items-center gap-3 overflow-hidden bg-white p-4 dark:bg-[#1c1c1c]">
          <div className="flex min-w-0 flex-1 items-center gap-[6px]">
            <Lock size={16} className="text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]" />
            <span className="whitespace-nowrap font-['Plus_Jakarta_Sans'] text-[13px] font-semibold leading-[18px] text-[#2d2923] dark:text-[#fdfdfd]">
              Total Locked
            </span>
          </div>
          <span className="font-['Plus_Jakarta_Sans'] text-[18px] font-medium leading-[24px] tracking-[-0.2px] text-[#2d2923] dark:text-[#fdfdfd]">
            {renderMetricValue(unlockData.total_locked)}
          </span>
        </div>

        <div className="flex h-[56px] items-center gap-3 overflow-hidden bg-white p-4 dark:bg-[#1c1c1c]">
          <div className="flex min-w-0 flex-1 items-center gap-[6px]">
            <Unlock size={16} className="text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]" />
            <span className="whitespace-nowrap font-['Plus_Jakarta_Sans'] text-[13px] font-semibold leading-[18px] text-[#2d2923] dark:text-[#fdfdfd]">
              Total Supply
            </span>
          </div>
          <span className="font-['Plus_Jakarta_Sans'] text-[18px] font-medium leading-[24px] tracking-[-0.2px] text-[#2d2923] dark:text-[#fdfdfd]">
            {renderMetricValue(unlockData.total_unlocked)}
          </span>
        </div>

        <div className="grid grid-cols-3 gap-4">
          <div className="flex h-[128px] flex-col justify-between bg-[rgba(255,255,255,0.4)] px-3 py-6 dark:bg-[rgba(23,23,23,0.4)]">
            <div className="flex items-center gap-[6px]">
              <Calendar size={14} className="text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]" />
              <span className="font-['Plus_Jakarta_Sans'] text-[13px] font-semibold leading-[18px] text-[rgba(45,41,35,0.8)] dark:text-[rgba(253,253,253,0.8)]">
                Vesting Start
              </span>
            </div>
            <span className="font-['Plus_Jakarta_Sans'] text-[16px] font-medium leading-[24px] text-[#2d2923] dark:text-[#fdfdfd]">
              {formatDate(unlockData.vesting_start_date)}
            </span>
          </div>
          <div className="flex h-[128px] flex-col justify-between bg-[rgba(255,255,255,0.4)] px-3 py-6 dark:bg-[rgba(23,23,23,0.4)]">
            <div className="flex items-center gap-[6px]">
              <Calendar size={14} className="text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]" />
              <span className="font-['Plus_Jakarta_Sans'] text-[13px] font-semibold leading-[18px] text-[rgba(45,41,35,0.8)] dark:text-[rgba(253,253,253,0.8)]">
                Vesting End
              </span>
            </div>
            <span className="font-['Plus_Jakarta_Sans'] text-[16px] font-medium leading-[24px] text-[#2d2923] dark:text-[#fdfdfd]">
              {formatDate(unlockData.vesting_end_date)}
            </span>
          </div>
          <div className="flex h-[128px] flex-col justify-between bg-[rgba(255,255,255,0.4)] px-3 py-6 dark:bg-[rgba(23,23,23,0.4)]">
            <div className="flex items-center gap-[6px]">
              <Calendar size={14} className="text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]" />
              <span className="font-['Plus_Jakarta_Sans'] text-[13px] font-semibold leading-[18px] text-[rgba(45,41,35,0.8)] dark:text-[rgba(253,253,253,0.8)]">
                Next Unlock
              </span>
            </div>
            <div>
              {unlockData.next_unlock_tokens > 0 && (
                <p className="font-['Plus_Jakarta_Sans'] text-[12px] font-medium leading-[16px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]">
                  {formatCompactNumber(unlockData.next_unlock_tokens)} tokens
                </p>
              )}
              <span className="font-['Plus_Jakarta_Sans'] text-[16px] font-medium leading-[24px] text-[#2d2923] dark:text-[#fdfdfd]">
                {unlockData.next_unlock_date ? formatDate(unlockData.next_unlock_date) : 'N/A'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {unlockData.allocations && unlockData.allocations.length > 0 && (
        <div className="border border-white bg-[rgba(255,255,255,0.32)] p-[6px] dark:border-[#171717] dark:bg-[rgba(23,23,23,0.32)]">
          <div className="space-y-[6px]">
            {unlockData.allocations.map((allocation, index) => {
              const unlockProgress = calculateUnlockProgress(allocation.tge_unlock_percent);
              const detailItems = [
                allocation.tge_unlock > 0
                  ? { label: 'TGE Unlock', value: formatCompactNumber(allocation.tge_unlock) }
                  : null,
                allocation.next_unlock_date
                  ? { label: 'Next Unlock Date', value: formatDate(allocation.next_unlock_date) }
                  : null,
                allocation.next_unlock_tokens > 0
                  ? { label: 'Next Unlock Amount', value: formatCompactNumber(allocation.next_unlock_tokens) }
                  : null,
              ].filter(Boolean) as Array<{ label: string; value: string }>;

              const firstRowDetails = detailItems.slice(0, 2);
              const secondRowDetail = detailItems[2];

              return (
                <div
                  key={index}
                  className="h-[341px] border border-[rgba(45,41,35,0.12)] bg-white px-[19px] py-[19px] dark:border-[rgba(253,253,253,0.12)] dark:bg-[#1c1c1c]"
                >
                  <div>
                    <h3 className="font-['Plus_Jakarta_Sans'] text-[16px] font-semibold leading-[24px] text-[#2d2923] dark:text-[#fdfdfd]">
                      {allocation.name}
                    </h3>
                    <div className="font-['Plus_Jakarta_Sans'] text-[13px] font-medium leading-[18px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]">
                      <span>{allocation.unlock_type.toLowerCase()}</span>
                      {allocation.tge_unlock_percent > 0 && (
                        <>
                          <span>{' • '}</span>
                          <span>{allocation.tge_unlock_percent}% TGE</span>
                        </>
                      )}
                    </div>
                  </div>

                  <div className="mt-4 flex justify-end">
                    <div className="w-[190px] text-right">
                      <p className="font-['Plus_Jakarta_Sans'] text-[18px] font-medium leading-[24px] tracking-[-0.2px] text-[#2d2923] dark:text-[#fdfdfd]">
                        {renderCurrencyWithMutedDollar(formatCompactCurrency(allocation.tokens))}
                      </p>
                      <p className="font-['Plus_Jakarta_Sans'] text-[12px] font-normal leading-[16px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]">
                        {allocation.allocation_of_supply.toFixed(1)}% of supply
                      </p>
                    </div>
                  </div>

                  <div className="mt-4">
                    <div className="mb-[10px] flex items-center justify-between">
                      <span className="font-['Plus_Jakarta_Sans'] text-[14px] font-medium leading-[20px] text-[#2d2923] dark:text-[#fdfdfd]">
                        Unlock Progress
                      </span>
                      <span className="font-['Plus_Jakarta_Sans'] text-[14px] font-medium leading-[20px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]">
                        {unlockProgress.toFixed(1)}% unlocked
                      </span>
                    </div>
                    <div className="h-[11px] overflow-hidden rounded-[8px] bg-[rgba(45,41,35,0.12)] dark:bg-[rgba(253,253,253,0.12)]">
                      <div
                        className="h-[11px] rounded-[8px] bg-[#2d2923] dark:bg-[#fdfdfd]"
                        style={{ width: `${unlockProgress}%` }}
                      />
                    </div>
                  </div>

                  <div className="mt-5 grid grid-cols-2 gap-[18px]">
                    {firstRowDetails.map((detail) => (
                      <div key={detail.label}>
                        <p className="font-['Plus_Jakarta_Sans'] text-[13px] font-medium leading-[18px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]">
                          {detail.label}
                        </p>
                        <p className="font-['Plus_Jakarta_Sans'] text-[14px] font-normal leading-[20px] text-[#2d2923] dark:text-[#fdfdfd]">
                          {detail.value}
                        </p>
                      </div>
                    ))}
                  </div>

                  {secondRowDetail && (
                    <div className="mt-4">
                      <p className="font-['Plus_Jakarta_Sans'] text-[13px] font-medium leading-[18px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]">
                        {secondRowDetail.label}
                      </p>
                      <p className="font-['Plus_Jakarta_Sans'] text-[14px] font-normal leading-[20px] text-[#2d2923] dark:text-[#fdfdfd]">
                        {secondRowDetail.value}
                      </p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectUnlocksMobile;
